import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { BoardShapes } from '../../../types/device';

interface BoardShapeListProps {
  shapes: BoardShapes;
  /**
   * Where each line's LEDs actually start, the derived ones included. Shown as
   * the placeholder of the field that overrides it, so a gym can see the
   * numbering it is about to change.
   */
  ledStarts: number[];
  selected: number | null;
  onSelect: (index: number | null) => void;
  onChange: (shapes: BoardShapes) => void;
}

/**
 * The lines a wall is drawn with, as a list beside the picture they are drawn
 * on: how many holds each carries, where its LEDs start, and what order they
 * run in.
 *
 * The order is the whole point of the list. It is the order the strip travels,
 * which is what numbers the LEDs — so moving a line up or down renumbers every
 * line after it, and that is the only way to correct a wall drawn out of order
 * without redrawing it.
 *
 * The number of holds is here rather than on the picture because it is a count,
 * not a place: a gym counts the holes along a panel and types it. Zero is a real
 * answer and a common one — it makes the line a path across to the next panel,
 * which carries no holds and lights no LED.
 */
export function BoardShapeList({
  shapes,
  ledStarts,
  selected,
  onSelect,
  onChange,
}: BoardShapeListProps) {
  const { t } = useTranslation();

  const replace = (index: number, shape: Partial<BoardShapes[number]>) =>
    onChange(shapes.map((current, at) => (at === index ? { ...current, ...shape } : current)));

  const move = (index: number, by: number) => {
    const to = index + by;
    if (to < 0 || to >= shapes.length) return;

    const next = [...shapes];
    [next[index], next[to]] = [next[to], next[index]];
    onChange(next);
    onSelect(to);
  };

  const remove = (index: number) => {
    onChange(shapes.filter((_, at) => at !== index));
    onSelect(null);
  };

  if (shapes.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        {t('gymAdmin.boardDashboard.grid.shapes.empty')}
      </p>
    );
  }

  return (
    <ul className="space-y-1">
      {shapes.map((shape, index) => (
        <li
          key={`${index}-${shape.from.u}-${shape.to.u}`}
          className={`rounded-md border p-2 ${
            index === selected ? 'border-primary bg-primary/5' : ''
          }`}
        >
          <div className="flex items-center gap-1">
            {/* The line's own number, and the way to point the picture at it. */}
            <button
              type="button"
              className="min-w-10 shrink-0 text-left text-xs font-medium"
              onClick={() => onSelect(index === selected ? null : index)}
              aria-pressed={index === selected}
            >
              {t('gymAdmin.boardDashboard.grid.shapes.line', { number: index + 1 })}
            </button>

            <label className="sr-only" htmlFor={`shape-holds-${index}`}>
              {t('gymAdmin.boardDashboard.grid.shapes.holds')}
            </label>
            <Input
              id={`shape-holds-${index}`}
              type="number"
              min={0}
              inputMode="numeric"
              className="h-8 w-16"
              value={shape.holds}
              onFocus={() => onSelect(index)}
              onChange={(event) =>
                replace(index, { holds: Math.max(0, Number(event.target.value) || 0) })
              }
            />

            <label className="sr-only" htmlFor={`shape-led-${index}`}>
              {t('gymAdmin.boardDashboard.grid.shapes.ledStart')}
            </label>
            <Input
              id={`shape-led-${index}`}
              type="number"
              min={0}
              inputMode="numeric"
              className="h-8 w-16"
              // Empty is not "no LED": it is the line carrying on from the one
              // before it, which is what the placeholder shows it would be.
              placeholder={String(ledStarts[index] ?? 0)}
              value={shape.led_start ?? ''}
              onFocus={() => onSelect(index)}
              onChange={(event) =>
                replace(index, {
                  led_start: event.target.value === '' ? null : Math.max(0, Number(event.target.value) || 0),
                })
              }
            />

            <div className="ml-auto flex shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                disabled={index === 0}
                aria-label={t('gymAdmin.boardDashboard.grid.shapes.moveUp', { number: index + 1 })}
                onClick={() => move(index, -1)}
              >
                <ArrowUp className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                disabled={index === shapes.length - 1}
                aria-label={t('gymAdmin.boardDashboard.grid.shapes.moveDown', { number: index + 1 })}
                onClick={() => move(index, 1)}
              >
                <ArrowDown className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 text-destructive"
                aria-label={t('gymAdmin.boardDashboard.grid.shapes.remove', { number: index + 1 })}
                onClick={() => remove(index)}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
